
export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'cancelled';

export type PaymentStatus = 'not_paid' | 'partially_paid' | 'fully_paid';

export type PaymentMethod =
  | 'cash'
  | 'upi'
  | 'bank_transfer'
  | 'google_pay'
  | 'cheque'
  | 'card'
  | 'online';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  cash: 'Cash',
  upi: 'UPI',
  bank_transfer: 'Bank Transfer',
  google_pay: 'Google Pay',
  cheque: 'Cheque',
  card: 'Card',
  online: 'Online',
};

export const INVOICE_STATUS_LABELS: Record<InvoiceStatus, string> = {
  draft: 'Remaining to share',
  sent: 'Shared',
  paid: 'Shared',
  cancelled: 'Cancelled',
};

export const PAYMENT_STATUS_LABELS: Record<string, string> = {
  not_paid: 'Not Paid',
  partially_paid: 'Partially Paid',
  fully_paid: 'Fully Paid',
  paid: 'Paid',
};

export interface InvoiceLineItem {
  id?: number;
  description: string;
  quantity: number;
  unit_price: string;
  order_index: number;
}

export interface InvoiceInstallment {
  id?: number;
  sequence: number;
  percent: string;
  label: string;
  due_date?: string;
  amount?: string;
  paid?: boolean;
  status?: string;
  payment_link_url?: string;
}

export interface InvoicePayment {
  id: number;
  amount: string;
  method: PaymentMethod;
  payment_date: string;
  reference?: string;
  notes?: string;
  installment_id?: number;
}
 
export interface Invoice {
  id: number;
  invoice_no: string;
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  customer_place?: string;
  customer_address?: string;
  invoice_date: string;
  due_date?: string;
  notes?: string;
  without_price: boolean;
  add_gst_number?: boolean;
  gst_number?: string;
  add_terms_conditions?: boolean;
  terms_and_conditions?: string | null;
  discount_percent: string;
  tax_percent: string;
  subtotal: string;
  discount_amount: string;
  tax_amount: string;
  grand_total: string;
  amount_paid: string;
  balance_due: string;
  remaining_amount?: string;
  invoice_status: InvoiceStatus;
  payment_status: PaymentStatus;
  line_items: InvoiceLineItem[];
  installments: InvoiceInstallment[];
  payments: InvoicePayment[];
  order_id?: number;
  branch_id?: number | null;
  branch_name?: string | null;
}

export interface InvoiceSummary {
  id: number;
  invoice_no: string;
  customer_name: string;
  customer_phone: string;
  invoice_date: string;
  due_date?: string;
  grand_total: string;
  amount_paid: string;
  balance_due: string;
  remaining_amount?: string;
  invoice_status: InvoiceStatus;
  payment_status: PaymentStatus;
  branch_id?: number | null;
  branch_name?: string | null;
}

export interface PaginatedInvoices {
  count: number;
  next: string | null;
  previous: string | null;
  results: InvoiceSummary[];
}

export interface InvoiceListParams {
  page?: number;
  page_size?: number;
  search?: string;
  invoice_status?: InvoiceStatus;
  payment_status?: PaymentStatus;
  from_date?: string;
  to_date?: string;
  branch_id?: number | null;
}

export interface InvoiceWritePayload {
  customer_name: string;
  customer_phone: string;
  customer_email?: string;
  customer_place?: string;
  customer_address?: string;
  invoice_date: string;
  due_date?: string;
  notes?: string;
  without_price?: boolean;
  add_gst_number?: boolean;
  gst_number?: string;
  add_terms_conditions?: boolean;
  terms_and_conditions?: string | null;
  discount_percent?: string;
  tax_percent?: string;
  line_items: InvoiceLineItem[];
  installments?: InvoiceInstallment[];
  branch_id?: number | null;
}

export interface CreateInvoiceFromOrderPayload {
  order_id: number;
}

export interface SetInstallmentsPayload {
  installments: InvoiceInstallment[];
}

export interface RecordPaymentPayload {
  amount: string;
  method: PaymentMethod;
  payment_date: string;
  reference?: string;
  notes?: string;
  installment_id?: number;
}

export interface CreatePaymentLinkPayload {
  host_name: string;
}

export interface InvoiceStatusBreakdown {
  invoice_status: string;
  count: number;
  amount: string | number;
}

export interface PaymentStatusBreakdown {
  payment_status: string;
  count: number;
  amount: string | number;
}

export interface MonthlyTrend {
  month: string | number;
  count: number;
  total_amount: string | number;
  paid_amount: string | number;
  remaining_amount: string | number;
}

export interface PaymentMethodBreakdown {
  method: string;
  count: number;
  amount: string | number;
}

export interface InvoiceDashboardStats {
  total_invoices: number;
  total_amount: string | number;
  total_paid: string | number;
  total_remaining: string | number;
  invoice_status_breakdowns: InvoiceStatusBreakdown[];
  payment_status_breakdowns: PaymentStatusBreakdown[];
  monthly_trends: MonthlyTrend[];
  payment_methods_breakdown: PaymentMethodBreakdown[];
}

